import { useState, useEffect, useCallback } from 'react';
import { contractsService } from '../services/api';

export function useContracts({ enabled = true } = {}) {
    const [contracts, setContracts] = useState([]);
    const [loading, setLoading] = useState(enabled);
    const [error, setError] = useState(null);

    const transformContract = (contract) => {
        const profileObj = contract.profile;
        const totalValue = parseFloat(contract.contractValue || 0);
        const totalPaid = parseFloat(contract.paidAmount || 0);
        const totalDue = Math.max(0, totalValue - totalPaid);
        
        return {
            ...contract,
            clientName: profileObj?.clientName || profileObj?.contactPersonName || contract.title || 'Client #' + contract.id,
            phone: profileObj?.mobile || profileObj?.phone || '',
            contractDate: contract.createdAt ? String(contract.createdAt).slice(0, 10) : '',
            expiryDate: contract.endDate ? String(contract.endDate).slice(0, 10) : null,
            startDate: contract.startDate ? String(contract.startDate).slice(0, 10) : (contract.createdAt ? String(contract.createdAt).slice(0, 10) : null),
            serviceType: contract.contractType || '',
            projectName: profileObj?.projectName || contract.title || '',
            orderNo: contract.contractNumber || contract.id,
            totalValue,
            totalPaid,
            totalDue,
            paymentStatus: contract.paymentStatus ? contract.paymentStatus.toLowerCase() : 'pending'
        };
    };

    const fetchContracts = useCallback(async (params = {}) => {
        setLoading(true);
        try {
            const fetchParams = { ...params };
            Object.keys(fetchParams).forEach(k => {
                if (fetchParams[k] === '' || fetchParams[k] === null || fetchParams[k] === undefined || fetchParams[k] === 'all') {
                    delete fetchParams[k];
                }
            });
            const response = await contractsService.getAll(fetchParams);
            const transformedContracts = (response.data || []).map(transformContract);
            setContracts(transformedContracts);
            setError(null);
        } catch (err) {
            console.error('Error fetching contracts:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (enabled) {
            fetchContracts();
        } else {
            setLoading(false);
        }
    }, [fetchContracts, enabled]);

    const addContract = async (contractData) => {
        const newContract = await contractsService.create(contractData);
        const transformed = transformContract(newContract);
        setContracts(prev => [transformed, ...prev]);
        return newContract;
    };

    const updateContract = async (id, contractData) => {
        const updated = await contractsService.update(id, contractData);
        const transformed = transformContract(updated);
        setContracts(prev => prev.map(c => c.id === id ? { ...c, ...transformed } : c));
        return updated;
    };

    const extendContract = async (id, newEndDate, reason = '') => {
        const response = await contractsService.extend(id, { newEndDate, reason });
        const updated = response.data || response;
        const transformed = transformContract(updated);
        setContracts(prev => prev.map(c => c.id === id ? { ...c, ...transformed } : c));
        return transformed;
    };

    const terminateContract = async (id, reason = '') => {
        const response = await contractsService.terminate(id, { reason });
        const updated = response.data || response;
        const transformed = transformContract(updated);
        setContracts(prev => prev.map(c => c.id === id ? { ...c, ...transformed } : c));
        return transformed;
    };

    return {
        contracts,
        loading,
        error,
        fetchContracts,
        addContract,
        updateContract,
        extendContract,
        terminateContract,
    };
}
